import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { ExpiredIcon } from '@/assets/icons/expired';
import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { SentEmailIcon } from '@/assets/icons/sentEmail';
import EnterCode from '@/components/authentication/modals/enter-code';
import { ConfirmationStatus } from '@/components/authentication/statuses/confirmation';
import { ErrorStatus } from '@/components/authentication/statuses/error';
import { Banner } from '@/components/basicComponents/banner';
import Button from '@/components/basicComponents/button';
import Input from '@/components/basicComponents/input';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import { UPDATE_USER_EMAIL_MUTATION } from '@/components/settings-page/users/queries';
import { useErrorMessages } from '@/hooks/i18n';
import { useAuth } from '@/hooks/use-auth';
import { themeVar } from '@/styles/theme';
import { useMutation } from '@apollo/client';
import axios from 'axios';
import { getMultiFactorResolver, MultiFactorResolver, PhoneAuthProvider, PhoneMultiFactorGenerator } from 'firebase/auth';
import { Helmet as Head } from 'react-helmet';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TwoFactorAuthentication, TwoFactorAuthenticationDescription, TwoFactorAuthenticationTitle } from './TwoFactorAuthentication';

interface IFormInput {
  password: string;
}

enum VERIFY_EMAIL_CHANGE_STATUS_ENUM {
  DEFAULT = 'default',
  CONFIRMATION = 'confirmation',
  EXPIRED = 'expired',
  SENT = 'sent',
  ERROR = 'error'
}

const VALIDATE_USERLESS_JWT_TOKEN_EXPIRATION_QUERY = `
  query validateUserlessJwtTokenExpiration($jwt: String!) {
    validateUserlessJwtTokenExpiration(jwt: $jwt)
  }
`;

const VerifyEmailChangePage = () => {
  const navigate = useNavigate();
  const { default: defaultErrorMessage } = useErrorMessages();
  const { auth, user, signInWithEmailAndPassword, signOut } = useAuth();
  const {
    control,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm<IFormInput>();

  const [searchParams, setSearchParams] = useSearchParams();

  const newEmail = searchParams.get('newEmail') || '';
  const existingUserEmail = searchParams.get('existingUserEmail') || '';
  const expiresAtJwt = searchParams.get('expiresAtJwt') || '';

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<VERIFY_EMAIL_CHANGE_STATUS_ENUM>(VERIFY_EMAIL_CHANGE_STATUS_ENUM.DEFAULT);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [validateUserlessJwtTokenExpirationLoading, setValidateUserlessJwtTokenExpirationLoading] = useState(false);
  const [verificationId, setVerificationId] = useState<string>();
  const [multiFactorResolver, setMultiFactorResolver] = useState<MultiFactorResolver | null>();
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: 'enter-code' });
  const [isEmailUpdatePending, setIsEmailUpdatePending] = useState(false);

  const [updateUserEmail, { loading: updateUserEmailLoading }] = useMutation(UPDATE_USER_EMAIL_MUTATION);

  useEffect(() => {
    if (!expiresAtJwt) return;

    (async () => {
      try {
        setValidateUserlessJwtTokenExpirationLoading(true);
        const { data: setNewPasswordTokenData } = await axios.post(`${process.env.REACT_APP_BACKEND_BASE_URL}/graphql`, {
          query: VALIDATE_USERLESS_JWT_TOKEN_EXPIRATION_QUERY,
          variables: {
            jwt: expiresAtJwt
          }
        });

        if (setNewPasswordTokenData?.data?.validateUserlessJwtTokenExpiration === 'success') {
          setStatus(VERIFY_EMAIL_CHANGE_STATUS_ENUM.DEFAULT);
          return;
        }

        if (setNewPasswordTokenData?.errors) {
          setStatus(VERIFY_EMAIL_CHANGE_STATUS_ENUM.ERROR);
          return;
        }
      } catch (error) {
        console.log(error);
      } finally {
        setValidateUserlessJwtTokenExpirationLoading(false);
      }
    })();
  }, [expiresAtJwt]);

  useEffect(() => {
    if (!user || !isEmailUpdatePending) return;

    (async () => {
      try {
        await updateUserEmail({
          variables: {
            data: {
              email: newEmail,
              expiresAtJwt
            }
          }
        });

        setIsEmailUpdatePending(false);
        handleStatusChange(VERIFY_EMAIL_CHANGE_STATUS_ENUM.CONFIRMATION);
      } catch (error) {
        setErrorMessage(defaultErrorMessage);
      }
    })();
  }, [user]);

  const handleStatusChange = (newStatus: VERIFY_EMAIL_CHANGE_STATUS_ENUM) => {
    setStatus(newStatus);
  };

  const openModalWindow = (type: string) => {
    setModalWindow({ type, isOpen: true });
  };

  const closeModalWindow = () => {
    setModalWindow({ ...modalWindow, isOpen: false });
  };

  const sendNewLink = async () => {
    try {
      setLoading(true);

      // if (data?.errors) {
      //   setStatus(VERIFY_EMAIL_CHANGE_STATUS_ENUM.ERROR);
      //   return;
      // }

      setStatus(VERIFY_EMAIL_CHANGE_STATUS_ENUM.SENT);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = () => {
    signOut();
    navigate('/');
  };

  const onSubmit = async (data: IFormInput) => {
    try {
      const { password } = data;
      setErrorMessage(null);
      setLoading(true);

      await signInWithEmailAndPassword(existingUserEmail, password);
      setIsEmailUpdatePending(true);
    } catch (error: any) {
      if (error.code == 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(auth, error);
        setMultiFactorResolver(resolver);
        if (resolver.hints[0].factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
          const phoneInfoOptions = {
            multiFactorHint: resolver.hints[0],
            session: resolver.session
          };
          const phoneAuthProvider = new PhoneAuthProvider(auth);

          try {
            const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions);
            setVerificationId(verificationId);
            openModalWindow('enter-code');
          } catch (error) {
            console.log(error);
          }

          return;
        }

        setError('password', { message: 'We do not recognize your login credentials' });
        return;
      }

      if (error.code == 'auth/wrong-password' || error.code == 'auth/user-not-found') {
        setError('password', { message: 'We do not recognize your login credentials' });
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  const verify2FACode = async (verificationCode: string) => {
    if (!verificationCode || !verificationId || !multiFactorResolver) {
      throw new Error(defaultErrorMessage);
    }

    const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
    await multiFactorResolver.resolveSignIn(multiFactorAssertion);

    setIsEmailUpdatePending(true);
  };

  if (validateUserlessJwtTokenExpirationLoading) return <LoaderOnWholeScreen />;

  const getContentByStatus = () => {
    switch (status) {
      case VERIFY_EMAIL_CHANGE_STATUS_ENUM.EXPIRED:
        return (
          <>
            <TwoFactorAuthentication>
              <ExpiredIcon width={175} height={90} />
              <TwoFactorAuthenticationTitle>Expired Link</TwoFactorAuthenticationTitle>
              <TwoFactorAuthenticationDescription>
                The link for this download has expired. We will send you a fresh link to your email address.
              </TwoFactorAuthenticationDescription>
            </TwoFactorAuthentication>
            <Button size="md" onClick={sendNewLink} styleType={loading ? 'disabled' : 'default'} isLoading={loading}>
              Send New Link
            </Button>
          </>
        );
      case VERIFY_EMAIL_CHANGE_STATUS_ENUM.SENT:
        return (
          <div>
            <TwoFactorAuthentication>
              <ExpiredIcon width={175} height={90} />
              <TwoFactorAuthenticationTitle>Expired Link</TwoFactorAuthenticationTitle>
              <TwoFactorAuthenticationDescription>
                The link for this download has expired. We will send you a fresh link to your email address.
              </TwoFactorAuthenticationDescription>
            </TwoFactorAuthentication>
            <BannerWrapper>
              <Banner
                title="Sent!"
                description="Your new link has been sent."
                icon={<ApprovedIcon fill={themeVar('context.light')} />}
                bgColor={themeVar('context.success')}
              />
            </BannerWrapper>
          </div>
        );
      case VERIFY_EMAIL_CHANGE_STATUS_ENUM.CONFIRMATION:
        return <ConfirmationStatus />;
      case VERIFY_EMAIL_CHANGE_STATUS_ENUM.ERROR:
        return <ErrorStatus />;
      default:
        return (
          <VerifyEmailForm noValidate onSubmit={handleSubmit(onSubmit)}>
            <SentEmailIcon />
            <FormLabel>Change Email</FormLabel>
            {errorMessage && (
              <Banner
                icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                title="Error"
                description={errorMessage}
                bgColor={themeVar('context.error')}
              />
            )}
            <EmailDetailsWrapper>
              <EmailDetails>
                Previous Email Address:
                <p style={{ fontWeight: 700 }}>{existingUserEmail}</p>
              </EmailDetails>
              <EmailDetails>
                New Email Address:
                <p style={{ fontWeight: 700 }}>{newEmail}</p>
              </EmailDetails>
              <VerifiedEmailText>
                <ApprovedIcon width={23} height={23} />
                Verified Email Address
              </VerifiedEmailText>
            </EmailDetailsWrapper>
            <Controller
              name="password"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Password is required'
                }
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Input
                  label="Enter Your Password"
                  value={value || ''}
                  onChange={(e) => {
                    onChange(e.target.value);
                  }}
                  type="password"
                  errorText={error?.message}
                  size="md"
                  withPasswordIcon
                  isRequiredField
                />
              )}
            />
            <Divider />
            <Button
              type="submit"
              size="md"
              width="max-content"
              styleType={!!Object.keys(errors).length || loading || updateUserEmailLoading ? 'disabled' : 'default'}
              isLoading={loading || updateUserEmailLoading}
            >
              Save Changes
            </Button>
          </VerifyEmailForm>
        );
    }
  };

  return (
    <>
      <Head>
        <title>Verify Email</title>
      </Head>
      {modalWindow.isOpen && modalWindow.type === 'enter-code' && (
        <EnterCode isOpen={modalWindow.isOpen} verify2FACode={verify2FACode} onClose={closeModalWindow} />
      )}
      {getContentByStatus()}
      <RecaptchaContainer id="verify-email-recaptcha-container" />
    </>
  );
};

export default VerifyEmailChangePage;

const VerifyEmailForm = styled.form`
  max-width: 420px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 0 20px;
`;

const FormLabel = styled.p`
  text-align: center;
  font-size: 33px;
  font-weight: 400;
  line-height: 46.2px;
  color: ${themeVar('font.strong')};
`;

const EmailDetailsWrapper = styled.div`
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const EmailDetails = styled.div`
  text-align: center;
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
  color: ${themeVar('font.base')};
`;

const VerifiedEmailText = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: ${themeVar('context.success')};
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  margin: 20px 0;
  background-color: ${themeVar('border.base')};
`;

const BannerWrapper = styled.div`
  margin-top: 30px;
`;

const RecaptchaContainer = styled.div``;
